//
// # LayerPanel
//
// IDE sidebar panel showing PDK layer visibility controls.
// Displays layers grouped by category (metal, via, etc.) with
// color swatches and click-to-toggle visibility.
//
// - Click: toggle individual layer visibility
// - Cmd/Ctrl+Click: solo mode (show only that layer)
// - "Show All" button: restore all layers to visible
//
// Below the per-layer rows, the panel renders a category-grouped grid where
// each row pairs a parent visibility toggle (left column) with a "Select X"
// toggle (right column). Indented sub-toggles (label overlays etc.) appear
// underneath the parent and are disabled when the parent is off.
//

import React, { useCallback } from "react";
import { Layers } from "lucide-react";
import {
  type LayoutDisplayOptions,
  type SchematicDisplayOptions,
  type LayoutSelectability,
  type SchematicSelectability,
  isMac,
  hasPlatformCmdModifier,
} from "EditorCore";
import { useLayerInfo } from "./useLayerInfo";
import { useSchematicDisplayInfo } from "./useSchematicDisplayInfo";
import "./layerPanel.css";

// Description of a category row in the two-column display/selectable grid.
// `visKey` toggles parent visibility (also gates sub-toggles + selectability).
// `selKey` toggles the "Select X" right-column checkbox.
// `subs` are the indented overlay/label toggles displayed beneath the parent.
interface CategoryRow<V extends string, S extends string> {
  label: string;
  visKey: V;
  selKey: S;
  subs: ReadonlyArray<{ key: V; label: string }>;
}

// Schematic categories, in display order. Sub-toggles are existing label
// overlays grouped under the category they belong to.
const schematicCategories: ReadonlyArray<
  CategoryRow<keyof SchematicDisplayOptions, keyof SchematicSelectability>
> = [
  {
    label: "Instances",
    visKey: "instances",
    selKey: "instances",
    subs: [
      { key: "instNames", label: "Instance Names" },
      { key: "cellNames", label: "Cell Names" },
      { key: "instParams", label: "Instance Params" },
    ],
  },
  {
    label: "Pins",
    visKey: "pins",
    selKey: "pins",
    subs: [
      { key: "portNames", label: "Port Names" },
      { key: "portDebug", label: "Port Debug" },
    ],
  },
  {
    label: "Wires",
    visKey: "wires",
    selKey: "wires",
    subs: [{ key: "wireLabels", label: "Wire Labels" }],
  },
];

// Layout categories, in display order. Stack and Abstract View are standalone
// (no selectability counterpart) and render below the grid.
const layoutCategories: ReadonlyArray<
  CategoryRow<keyof LayoutDisplayOptions, keyof LayoutSelectability>
> = [
  {
    label: "Instances",
    visKey: "instances",
    selKey: "instances",
    subs: [{ key: "instanceLabels", label: "Instance Labels (5)" }],
  },
  {
    label: "Pins",
    visKey: "pins",
    selKey: "pins",
    subs: [{ key: "pinLabels", label: "Pin Labels (4)" }],
  },
  {
    label: "Routes",
    visKey: "routes",
    selKey: "routes",
    subs: [{ key: "routeLabels", label: "Route Labels (6)" }],
  },
  {
    label: "Blockages",
    visKey: "blockages",
    selKey: "blockages",
    subs: [],
  },
];

// Generic checkbox row. Renders a label + checkbox; supports a disabled state
// (greyed out, non-interactive) for sub-toggles whose parent is hidden.
interface CheckboxRowProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  // When true, the row is indented under a parent visibility toggle.
  isSub?: boolean;
  // When true, the row is greyed out and clicks are ignored.
  disabled?: boolean;
}

function CheckboxRow({
  label,
  checked,
  onToggle,
  isSub,
  disabled,
}: CheckboxRowProps) {
  const className = [
    "layer-panel-checkbox-row",
    isSub ? "layer-panel-sub-row" : "",
    disabled ? "is-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <label className={className}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled ?? false}
        onChange={() => {
          if (!disabled) onToggle();
        }}
      />
      <span className="layer-panel-checkbox-label">{label}</span>
    </label>
  );
}

// Two-column display/selectable grid shared by the schematic and layout
// sections. Each row pairs a parent visibility toggle (left) with a
// "Select X" toggle (right); sub-toggles appear indented underneath and
// inherit the parent's disabled state when the parent is hidden.
//
// Generic over the display option key type `V` and selectability key type
// `S` so the same component renders both schematic and layout grids.
interface DisplaySelectGridProps<V extends string, S extends string> {
  title: string;
  categories: ReadonlyArray<CategoryRow<V, S>>;
  display: Record<V, boolean>;
  selectability: Record<S, boolean>;
  toggleDisplay: (key: V) => void;
  toggleSelectability: (key: S) => void;
}

function DisplaySelectGrid<V extends string, S extends string>({
  title,
  categories,
  display,
  selectability,
  toggleDisplay,
  toggleSelectability,
}: DisplaySelectGridProps<V, S>) {
  return (
    <div className="layer-panel-display-options">
      <div className="layer-panel-category">{title}</div>
      <div className="layer-panel-grid">
        {categories.map((cat) => {
          const parentVisible = display[cat.visKey];
          return (
            <div key={cat.label} className="layer-panel-category-block">
              {/* Parent visibility (left) + Select X (right) */}
              <CheckboxRow
                label={`Show ${cat.label}`}
                checked={parentVisible}
                onToggle={() => {
                  toggleDisplay(cat.visKey);
                }}
              />
              <CheckboxRow
                label={`Select ${cat.label}`}
                checked={selectability[cat.selKey]}
                disabled={!parentVisible}
                onToggle={() => {
                  toggleSelectability(cat.selKey);
                }}
              />
              {/* Sub-toggles, indented under the parent in the left column */}
              {cat.subs.length > 0 && (
                <div className="layer-panel-sub-rows">
                  {cat.subs.map((sub) => (
                    <CheckboxRow
                      key={sub.key}
                      label={sub.label}
                      checked={display[sub.key]}
                      disabled={!parentVisible}
                      isSub
                      onToggle={() => {
                        toggleDisplay(sub.key);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LayerPanel() {
  const {
    layersByCategory,
    pdkName,
    stackName,
    allLayerNumbers,
    hiddenLayers,
    layoutDisplay,
    showAbstractView,
    showUsedLayersOnly,
    selectability: layoutSelectability,
    toggleLayer,
    showOnlyLayer,
    showAllLayers,
    hideAllLayers,
    toggleLayoutDisplay,
    toggleAbstractView,
    toggleShowUsedLayersOnly,
    toggleSelectability: toggleLayoutSelectability,
  } = useLayerInfo();

  const schematicDisplay = useSchematicDisplayInfo();
  const schematicOpts = schematicDisplay.options;
  const schematicSelectability = schematicDisplay.selectability;

  const handleRowClick = useCallback(
    (e: React.MouseEvent, layerNumber: number) => {
      if (hasPlatformCmdModifier(e)) {
        // Solo mode: show only this layer
        showOnlyLayer(layerNumber);
      } else {
        toggleLayer(layerNumber);
      }
    },
    [toggleLayer, showOnlyLayer]
  );

  const hasHiddenLayers = hiddenLayers.size > 0;
  const hasVisibleLayers = hiddenLayers.size < allLayerNumbers.length;

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <Layers size={11} />
          <span className="flex-1">Layers</span>
          {hasHiddenLayers && (
            <button
              className="layer-panel-show-all-btn"
              onClick={showAllLayers}
              title="Show all layers (A)"
            >
              Show All
            </button>
          )}
          {hasVisibleLayers && (
            <button
              className="layer-panel-show-all-btn"
              onClick={hideAllLayers}
              title="Hide all layers"
            >
              Hide All
            </button>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {!layersByCategory && !schematicDisplay.isSchematic && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            Open a design to view display options.
          </div>
        )}

        {layersByCategory && layersByCategory.size === 0 && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            No layers found.
          </div>
        )}

        {layersByCategory && layersByCategory.size > 0 && (
          <div className="p-1">
            {/* PDK/Stack info */}
            {pdkName && (
              <div className="px-2 pb-1 text-[10px] text-[var(--color-ide-text-muted)] italic">
                {pdkName}
                {stackName && ` / ${stackName}`}
              </div>
            )}

            {/* Filter: show only used layers */}
            <label className="layer-panel-checkbox-row">
              <input
                type="checkbox"
                checked={showUsedLayersOnly}
                onChange={toggleShowUsedLayersOnly}
              />
              <span className="layer-panel-checkbox-label">
                Used layers only
              </span>
            </label>

            {/* Layer categories */}
            {Array.from(layersByCategory.entries()).map(
              ([category, layers]) => (
                <div key={category}>
                  <div className="layer-panel-category">{category}</div>
                  {layers.map((layer) => {
                    const isHidden = hiddenLayers.has(layer.number);
                    const color = layer.color;
                    const bgColor = color
                      ? `rgba(${color.r}, ${color.g}, ${color.b}, ${
                          (color.a ?? 255) / 255
                        })`
                      : "rgba(128, 128, 128, 1)";

                    return (
                      <div
                        key={layer.number}
                        className={`layer-panel-row${
                          isHidden ? " layer-hidden" : ""
                        }`}
                        onClick={(e) => handleRowClick(e, layer.number)}
                        title={`${layer.name} (#${layer.number})${
                          isHidden ? " [hidden]" : ""
                        }\nClick to toggle, ${
                          isMac ? "Cmd" : "Ctrl"
                        }+Click for solo`}
                      >
                        <div
                          className="layer-panel-swatch"
                          style={{ backgroundColor: bgColor }}
                        />
                        <div className="layer-panel-name">{layer.name}</div>
                        <div className="layer-panel-number">
                          #{layer.number}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Schematic Display / Selectable grid */}
        {schematicDisplay.isSchematic &&
          schematicOpts &&
          schematicSelectability && (
            <DisplaySelectGrid
              title="Schematic Display / Selectable"
              categories={schematicCategories}
              display={schematicOpts}
              selectability={schematicSelectability}
              toggleDisplay={schematicDisplay.toggle}
              toggleSelectability={schematicDisplay.toggleSelectability}
            />
          )}

        {/* Layout Display / Selectable grid, plus a standalone Abstract View
            row below the grid (no selectability counterpart). */}
        {layersByCategory && layoutDisplay && layoutSelectability && (
          <>
            <DisplaySelectGrid
              title="Layout Display / Selectable"
              categories={layoutCategories}
              display={layoutDisplay}
              selectability={layoutSelectability}
              toggleDisplay={toggleLayoutDisplay}
              toggleSelectability={toggleLayoutSelectability}
            />
            <div className="layer-panel-display-options">
              <CheckboxRow
                label="Abstract View (8)"
                checked={showAbstractView}
                onToggle={toggleAbstractView}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
